// CallbackLogs — payment webhook callback history
import { useState, useCallback } from "react";
import {
  Loader2,
  Inbox,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";
import {
  useCallbackLogsQuery,
} from "@/hooks/admin/usePayments";
import type { CallbackLog } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function truncate(s: string | null | undefined, len: number): string {
  if (!s) return "—";
  return s.length > len ? `${s.slice(0, len)}…` : s;
}

// ---------------------------------------------------------------------------
// Provider badge
// ---------------------------------------------------------------------------

const PROVIDER_LABELS: Record<string, string> = {
  wave: "Wave",
  orange_money: "Orange Money",
};

const PROVIDER_COLORS: Record<string, string> = {
  wave: "bg-emerald-100 text-emerald-800",
  orange_money: "bg-orange-100 text-orange-800",
};

function ProviderBadge({ provider }: { readonly provider: string }) {
  const label = PROVIDER_LABELS[provider] ?? provider;
  const color = PROVIDER_COLORS[provider] ?? "bg-stone-100 text-stone-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ log }: { readonly log: CallbackLog }) {
  if (log.error) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
        Erreur
      </span>
    );
  }
  if (log.processedAt) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
        Traité
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      En attente
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const PROVIDERS = [
  { value: "", label: "Tous" },
  { value: "wave", label: "Wave" },
  { value: "orange_money", label: "Orange Money" },
];

const PAGE_SIZE = 25;

export default function CallbackLogs() {
  const [provider, setProvider] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useCallbackLogsQuery({
    provider: provider || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const callbacks = data?.callbacks ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleProviderChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setProvider(e.target.value);
      setPage(1);
    },
    [],
  );

  const handlePrev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const handleNext = useCallback(
    () => setPage((p) => Math.min(totalPages, p + 1)),
    [totalPages],
  );

  return (
    <AdminLayout>
      <ModuleGuard moduleKey="wave-callbacks">
        <div className="space-y-6 p-6">
          {/* Header */}
          <div>
            <h1 className="text-xl font-semibold text-stone-900">
              Logs des callbacks
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Historique des webhooks de paiement reçus
            </p>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-3">
            <label
              htmlFor="provider-filter"
              className="text-sm font-medium text-stone-700"
            >
              Fournisseur
            </label>
            <select
              id="provider-filter"
              value={provider}
              onChange={handleProviderChange}
              className="rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary"
            >
              {PROVIDERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
              </div>
            ) : callbacks.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <Inbox className="h-10 w-10 text-stone-300" />
                <p className="text-sm text-muted-foreground">
                  Aucun callback enregistré
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-stone-200 bg-stone-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Fournisseur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Réf. transaction
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Signature
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      IP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {callbacks.map((log) => (
                    <CallbackRow key={log.id} log={log} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && total > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {total} résultat{total !== 1 ? "s" : ""} — page {page} sur{" "}
                {totalPages}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePrev}
                  disabled={page <= 1}
                  aria-label="Page précédente"
                  className="rounded-md p-2 hover:bg-muted transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={handleNext}
                  disabled={page >= totalPages}
                  aria-label="Page suivante"
                  className="rounded-md p-2 hover:bg-muted transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </ModuleGuard>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Row component — extracted to keep table body readable
// ---------------------------------------------------------------------------

function CallbackRow({ log }: { readonly log: CallbackLog }) {
  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <td className="whitespace-nowrap px-4 py-3 text-sm text-stone-700">
        {formatDateTime(log.createdAt)}
      </td>
      <td className="px-4 py-3">
        <ProviderBadge provider={log.provider} />
      </td>
      <td className="px-4 py-3">
        <span
          className="cursor-default font-mono text-xs text-stone-700"
          title={log.transactionRef ?? undefined}
        >
          {truncate(log.transactionRef, 12)}
        </span>
      </td>
      <td className="px-4 py-3">
        {log.signatureValid === null ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : log.signatureValid ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-label="Valide" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500" aria-label="Invalide" />
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge log={log} />
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {log.ipAddress ?? "—"}
      </td>
    </tr>
  );
}
